import { useLayer } from '@statsig/react-bindings';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useGetPatientNextAppointment } from '@grow-therapy-team/seedling-components';

import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { ExperimentLayers } from '../../statsig/experiments';
import { useOpenClientInformationCallback } from '../client-information/useClientInformationDrawerCallbacks';
import { useAppointmentByShortId } from '../schedule-appointment/useInSessionAppointment';
import { AppointmentType } from '../schedule-preview/types';
import { ProviderDrawerState, prevSessionPatientInfoAtom, providerShortIdAtom } from '../state';

export function useWaitingRoomScheduleNudge() {
  const { userClicked, userViewed } = useTrackEvent();
  const providerShortId = useAtomValue(providerShortIdAtom);
  const openClientInformation = useOpenClientInformationCallback();

  const prevSessionPatientInfo = useAtomValue(prevSessionPatientInfoAtom);
  const completedAppointment = useAppointmentByShortId(prevSessionPatientInfo?.inSessionAppointmentShortId);
  const isCompletedIntake =
    !!prevSessionPatientInfo && completedAppointment?.appointmentType === AppointmentType.Intake;

  // Only read the layer param once an intake has completed so exposure is logged at the same point in both arms.
  const [layerName, { shouldSeeWaitingRoomScheduleCta }] = ExperimentLayers.FollowUpScheduling;
  const layer = useLayer(layerName);
  const isTreatment = isCompletedIntake && (layer.get<boolean>(shouldSeeWaitingRoomScheduleCta, false) ?? false);

  const patientShortId = isTreatment ? prevSessionPatientInfo.patientShortId : undefined;
  const appointmentShortId = isTreatment ? completedAppointment.appointmentShortId : undefined;

  // Refetched by seedlings' appointment-creation mutation once the provider books a follow-up.
  const { data: nextAppointmentData, loading: isNextAppointmentLoading } = useGetPatientNextAppointment(patientShortId);
  const nextAppointment = nextAppointmentData?.patient?.nextAppointment;
  const hasFutureAppointment = !!nextAppointment;

  const trackingParams = useMemo(
    () => ({
      appointmentShortId: appointmentShortId ?? '',
      patientShortId: patientShortId ?? '',
      providerShortId: providerShortId ?? '',
      sessionType: 'intake' as const,
      hasFutureAppointment,
      // Nothing is tracked in the control arm, so this is always the treatment group.
      experimentGroup: 'true',
    }),
    [appointmentShortId, hasFutureAppointment, patientShortId, providerShortId],
  );

  const shownForAppointmentShortIdRef = useRef<string>();
  useEffect(
    function trackScheduleEntrypointShown() {
      if (!appointmentShortId || isNextAppointmentLoading || hasFutureAppointment) return;
      if (shownForAppointmentShortIdRef.current === appointmentShortId) return;
      shownForAppointmentShortIdRef.current = appointmentShortId;
      userViewed(EventTypeEnum.WAITING_ROOM_SCHEDULE_ENTRYPOINT_SHOWN, trackingParams);
    },
    [appointmentShortId, hasFutureAppointment, isNextAppointmentLoading, trackingParams, userViewed],
  );

  const onScheduleNextSession = useCallback(() => {
    if (!patientShortId) return;
    userClicked(EventTypeEnum.WAITING_ROOM_SCHEDULE_ENTRYPOINT_CLICKED, trackingParams);
    void openClientInformation({ patientShortId }, ProviderDrawerState.ADD_APPOINTMENT);
  }, [openClientInformation, patientShortId, trackingParams, userClicked]);

  return {
    completedSession: isTreatment
      ? {
          clientName: prevSessionPatientInfo.name ?? '',
          nextAppointmentTimeStart: nextAppointment ? String(nextAppointment.timeStart) : null,
          isNextAppointmentLoading,
        }
      : undefined,
    onScheduleNextSession,
  };
}
